
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DatabaseProgram } from "@/types/program";
import { duplicateProgram } from "@/services/program/programService";

export function useProgramDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [program, setProgram] = useState<any>(null);
  const [programData, setProgramData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  
  useEffect(() => {
    const fetchProgramDetail = async () => {
      if (!id) return;
      
      try {
        // First fetch the program
        const { data: programData, error: programError } = await supabase
          .from('programs')
          .select('*, tier_questions, ai_context')
          .eq('program_id', id)
          .single();
        
        if (programError) {
          console.error("Error fetching program:", programError);
          toast({
            title: "Error",
            description: "Failed to load program details",
            variant: "destructive",
          });
          return;
        }
        
        if (programData) {
          // Set the program data
          setProgram(programData);
          
          // Then fetch the program_data for any additional data
          const { data: additionalData, error: dataError } = await supabase
            .from('program_data')
            .select('data')
            .eq('program_id', id)
            .maybeSingle();
          
          if (dataError) {
            console.error("Error fetching program_data:", dataError);
            // Don't throw error here, continue with what we have
          } else if (additionalData) {
            console.log("Additional program data:", additionalData);
            setProgramData(additionalData.data);
          }
        } else {
          toast({
            title: "Not Found",
            description: "Program not found",
            variant: "destructive",
          });
          navigate('/programs');
        }
      } catch (error) {
        console.error("Error in fetchProgramDetail:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProgramDetail();
  }, [id, navigate, toast]);

  const handleDeleteProgram = async () => {
    if (!program?.program_id) return;
    
    setIsDeleting(true);
    try {
      // First delete from program_data (if it exists)
      const { error: dataError } = await supabase
        .from('program_data')
        .delete()
        .eq('program_id', program.program_id);
      
      if (dataError) {
        console.error("Error deleting program_data:", dataError);
        // Continue anyway, as this may not exist for all programs
      }
      
      // Then delete the program itself
      const { error } = await supabase
        .from('programs')
        .delete()
        .eq('program_id', program.program_id);
      
      if (error) {
        console.error("Error deleting program:", error);
        toast({
          title: "Error",
          description: `Failed to delete program: ${error.message}`,
          variant: "destructive",
        });
        return;
      }
      
      toast({
        title: "Success",
        description: "Program deleted successfully",
      });
      
      navigate('/programs');
    } catch (error) {
      console.error("Error in handleDeleteProgram:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while deleting program",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDuplicateProgram = async () => {
    if (!program?.program_id) return;
    
    setIsDuplicating(true);
    try {
      const newProgram = await duplicateProgram(program.program_id);
      
      toast({
        title: "Success",
        description: "Program duplicated successfully",
      });
      
      // Navigate to the new program
      navigate(`/programs/${newProgram.program_id}`);
    } catch (error) {
      console.error("Error in handleDuplicateProgram:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An unexpected error occurred while duplicating program",
        variant: "destructive",
      });
    } finally {
      setIsDuplicating(false);
    }
  };

  return {
    program,
    programData,
    loading,
    deleteDialogOpen,
    setDeleteDialogOpen,
    isDeleting,
    isDuplicating,
    handleDeleteProgram,
    handleDuplicateProgram
  };
}
